import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGift, 
  faCheck, 
  faCreditCard, 
  faSearch,
  faCalendar,
  faTimes,
  faStar,
  faDollarSign,
  faEnvelope,
  faUser,
  faDownload,
  faPrint,
  faQrcode,
  faPhone,
  faHome
} from '@fortawesome/free-solid-svg-icons';

const GiftCards = () => {
  // Geolocation state
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [isInCuba, setIsInCuba] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check user location
  useEffect(() => {
    const checkLocation = async () => {
      try {
        // Using ipapi.co free API for geolocation
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        // Check if user is in Cuba (country code: CU)
        if (data.country_code === 'CU') {
          setIsInCuba(true);
        }
      } catch (error) {
        console.error('Error checking location:', error);
        // If geolocation fails, allow access (fail open)
        setIsInCuba(false);
      } finally {
        setIsLoadingLocation(false);
      }
    };

    checkLocation();
  }, []);

  const [activeTab, setActiveTab] = useState('crear');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Estados para crear gift card
  const [giftCardData, setGiftCardData] = useState({
    valor: 50,
    comprador_nombre: '',
    comprador_email: '',
    comprador_telefono: '',
    destinatario_nombre: '',
    mensaje_personalizado: '',
    meses_validez: 12
  });

  // Estados para validar/usar gift card
  const [codigo, setCodigo] = useState('');
  const [montoUso, setMontoUso] = useState('');
  const [conceptoUso, setConceptoUso] = useState('');
  const [giftCardInfo, setGiftCardInfo] = useState(null);

  // Estados para la gift card creada y modal de descarga
  const [createdGiftCard, setCreatedGiftCard] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const giftCardRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleCrearGiftCard = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    console.log('🚀 Starting gift card creation...');
    console.log('📋 Gift card data:', giftCardData);
    console.log('🌐 Backend URL:', BACKEND_URL);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/gift-cards/crear`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(giftCardData)
      });
      
      console.log('📡 Response status:', response.status);
      const data = await response.json();
      console.log('📦 Response data:', data);
      
      if (response.ok && data.success) {
        console.log('✅ Success! Showing modal...');
        showMessage(`✅ Gift Card creada exitosamente! Código: ${data.gift_card.codigo}`, 'success');
        
        // Mostrar modal de descarga con la gift card creada
        setCreatedGiftCard(data.gift_card);
        setShowDownloadModal(true);
        console.log('🎉 Modal should be visible now');
        
        // Reset form
        setGiftCardData({
          valor: 50,
          comprador_nombre: '',
          comprador_email: '',
          comprador_telefono: '',
          destinatario_nombre: '',
          mensaje_personalizado: '',
          meses_validez: 12
        });
      } else {
        console.log('❌ Error response:', data);
        showMessage(`❌ Error: ${data.detail || 'Error creando gift card'}`, 'error');
      }
    } catch (error) {
      console.log('💥 Catch error:', error);
      showMessage(`❌ Error de conexión: ${error.message}`, 'error');
    } finally {
      setLoading(false);
      console.log('🏁 Loading finished');
    }
  };

  const handleValidarGiftCard = async () => {
    if (!codigo.trim()) {
      showMessage('❌ Por favor ingresa un código', 'error');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/gift-cards/validar/${codigo.trim()}`);
      const data = await response.json();
      
      if (response.ok) {
        setGiftCardInfo(data);
        if (data.valida) {
          showMessage(`✅ ${data.mensaje}`, 'success');
        } else {
          showMessage(`⚠️ ${data.mensaje}`, 'warning');
        }
      } else {
        showMessage(`❌ Error validando gift card`, 'error');
      }
    } catch (error) {
      showMessage(`❌ Error de conexión: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUsarGiftCard = async () => {
    if (!codigo.trim() || !montoUso) {
      showMessage('❌ Por favor completa todos los campos requeridos', 'error');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/gift-cards/usar/${codigo.trim()}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          codigo: codigo.trim(),
          monto_a_usar: parseFloat(montoUso),
          concepto: conceptoUso || 'Uso en restaurante'
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showMessage(`✅ ${data.message} Saldo restante: $${data.saldo_restante}`, 'success');
        // Limpiar campos
        setMontoUso('');
        setConceptoUso('');
        // Re-validar para mostrar nuevo saldo
        setTimeout(() => handleValidarGiftCard(), 1000);
      } else {
        showMessage(`❌ Error: ${data.detail || 'Error usando gift card'}`, 'error');
      }
    } catch (error) {
      showMessage(`❌ Error de conexión: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para imprimir la gift card
  const handlePrintGiftCard = () => {
    const printContent = giftCardRef.current;
    const originalDisplay = printContent.style.display;
    const originalBody = document.body.innerHTML;
    
    // Crear estilos de impresión
    const printStyles = `
      <style>
        @media print {
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Inter', sans-serif; background: white !important; }
          .gift-card-print { 
            width: 100%; 
            max-width: 600px; 
            margin: 0 auto;
            background: linear-gradient(135deg, #1f2937 0%, #111827 100%) !important;
            color: white !important;
            padding: 40px;
            border-radius: 20px;
            border: 2px solid #374151;
            page-break-inside: avoid;
          }
          .no-print { display: none !important; }
        }
      </style>
    `;
    
    // Preparar contenido para impresión
    document.head.insertAdjacentHTML('beforeend', printStyles);
    document.body.innerHTML = printContent.outerHTML;
    
    // Imprimir
    window.print();
    
    // Restaurar contenido original
    document.body.innerHTML = originalBody;
    window.location.reload(); // Recargar para restaurar eventos
  };

  // Función para descargar como PDF (usando html2canvas + jsPDF)
  const handleDownloadPDF = async () => {
    try {
      // Importar librerías dinámicamente
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      
      if (giftCardRef.current) {
        showMessage('🔄 Generando PDF...', 'info');
        
        const canvas = await html2canvas(giftCardRef.current, {
          backgroundColor: '#000000',
          scale: 2,
          useCORS: true,
          allowTaint: true
        });
        
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('landscape', 'mm', 'a4');
        
        const imgWidth = 280;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 15, 15, imgWidth, imgHeight);
        pdf.save(`ADASTRA_GiftCard_${createdGiftCard.codigo}.pdf`);
        
        showMessage('✅ PDF descargado exitosamente!', 'success');
      }
    } catch (error) {
      console.error('Error generando PDF:', error);
      showMessage('❌ Error generando PDF. Usa la opción de imprimir.', 'error');
    }
  };

  const valorPredefinidos = [25, 50, 75, 100, 150, 200, 250, 300, 500];

  return (
    <div className="min-h-screen bg-white">
      {/* Loading state while checking location */}
      {isLoadingLocation && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Verificando ubicación...</p>
          </div>
        </div>
      )}

      {/* Message for users in Cuba */}
      {!isLoadingLocation && isInCuba && (
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 md:p-12 text-center border-2 border-gray-200">
            <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <FontAwesomeIcon icon={faGift} className="w-10 h-10 text-gray-600" />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
              Gift Cards <span className="text-green-600 italic">No Disponibles</span>
            </h2>
            
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              El servicio no está disponible.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/"
                className="bg-gray-900 text-white border border-gray-900 py-4 px-8 rounded-2xl font-medium hover:bg-gray-800 transition-all duration-300 flex items-center justify-center space-x-2 transform hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                <FontAwesomeIcon icon={faHome} className="w-5 h-5" />
                <span>Volver al Home</span>
              </Link>
              
              <a 
                href="https://wa.me/5363518498?text=Hola%20ADASTRA,%20me%20interesa%20hacer%20una%20consulta."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-gray-900 border-2 border-gray-900 py-4 px-8 rounded-2xl font-medium hover:bg-gray-900 hover:text-white transition-all duration-300 flex items-center justify-center space-x-2 transform hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                <FontAwesomeIcon icon={faPhone} className="w-5 h-5" />
                <span>Contactar</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Main content - only show if not in Cuba and not loading */}
      {!isLoadingLocation && !isInCuba && (
        <>
      {/* Hero Section con diseño elegante - Fondo blanco */}
      <section className="relative min-h-screen bg-white overflow-hidden">
        <div className="relative z-10 flex flex-col items-center justify-start min-h-screen pt-44 px-4">
          
          {/* Header elegante - Estilo exacto como secciones del Home */}
          <div className="text-center mb-16">

            {/* Título principal - Estilo exacto del Home */}
            <div className="mb-6">
              <h2 className="font-sans text-3xl md:text-6xl font-light text-gray-900 leading-tight mb-6">
                Gift Cards <span className="text-green-600 font-normal italic">ADASTRA</span>
              </h2>
            </div>

            {/* Descripción - Estilo exacto del Home */}
            <p className="font-sans text-base md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
              Regala experiencias gastronómicas únicas. Crea y administra gift cards 
              para el restaurante más exclusivo de La Habana.
            </p>
          </div>

          {/* Message Alert - Invertido para fondo blanco */}
          {message && (
            <div className={`mb-8 p-4 rounded-lg backdrop-blur-sm border text-center font-medium max-w-2xl mx-auto ${
              messageType === 'success' ? 'bg-green-50 text-green-800 border-green-200' :
              messageType === 'error' ? 'bg-red-50 text-red-800 border-red-200' :
              messageType === 'warning' ? 'bg-yellow-50 text-yellow-800 border-yellow-200' :
              'bg-blue-50 text-blue-800 border-blue-200'
            }`}>
              {message}
            </div>
          )}

          {/* Navigation Tabs - Más visibles */}
          <div className="flex bg-gray-100 backdrop-blur-sm rounded-xl p-1 md:p-2 border-2 border-gray-200 mb-12 shadow-lg">
            <button
              onClick={() => setActiveTab('crear')}
              className={`flex-1 px-3 py-2 md:px-8 md:py-4 rounded-lg text-sm md:text-base font-medium transition-all duration-300 flex items-center justify-center space-x-1 md:space-x-2 ${
                activeTab === 'crear' 
                  ? 'bg-black text-white shadow-lg' 
                  : 'text-gray-700 hover:bg-gray-200 hover:text-black'
              }`}
            >
              <FontAwesomeIcon icon={faStar} className="w-3 h-3 md:w-4 md:h-4" />
              <span>Crear</span>
            </button>
            <button
              onClick={() => setActiveTab('usar')}
              className={`flex-1 px-3 py-2 md:px-8 md:py-4 rounded-lg text-sm md:text-base font-medium transition-all duration-300 flex items-center justify-center space-x-1 md:space-x-2 ${
                activeTab === 'usar'
                  ? 'bg-black text-white shadow-lg'
                  : 'text-gray-700 hover:bg-gray-200 hover:text-black'
              }`}
            >
              <FontAwesomeIcon icon={faSearch} className="w-3 h-3 md:w-4 md:h-4" />
              <span>Validar</span>
            </button>
          </div>

          {/* Content Container */}
          <div className="w-full max-w-4xl mx-auto">
            
            {/* Crear Gift Card Tab - Más contraste */}
            {activeTab === 'crear' && (
              <div className="bg-gray-50 backdrop-blur-sm rounded-2xl border-2 border-gray-200 p-4 md:p-8 lg:p-12 shadow-xl">
                <div className="flex items-center mb-4 md:mb-8">
                  <div className="bg-gray-200 backdrop-blur-sm rounded-full p-2 md:p-3 mr-3 md:mr-4 border border-gray-300">
                    <FontAwesomeIcon icon={faGift} className="h-4 w-4 md:h-6 md:w-6 text-gray-700" />
                  </div>
                  <h2 className="text-xl md:text-3xl font-light text-black">Crear Nueva Gift Card</h2>
                </div>
                
                <form onSubmit={handleCrearGiftCard} className="space-y-4 md:space-y-8">
                  {/* Valor */}
                  <div>
                    <label className="block text-black font-medium mb-2 md:mb-4 text-sm md:text-base flex items-center">
                      <FontAwesomeIcon icon={faDollarSign} className="w-3 h-3 md:w-4 md:h-4 mr-2 text-gray-600" />
                      Valor de la Gift Card *
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 md:gap-3 mb-4">
                      {valorPredefinidos.map(valor => (
                        <button
                          key={valor}
                          type="button"
                          onClick={() => setGiftCardData({...giftCardData, valor})}
                          className={`p-2 md:p-4 rounded-xl text-sm md:text-base font-medium transition-all duration-300 border-2 ${
                            giftCardData.valor === valor
                              ? 'bg-black text-white shadow-lg transform scale-105 border-black'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100 hover:text-black hover:border-gray-400 shadow-sm'
                          }`}
                        >
                          ${valor}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Comprador */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label className="block text-black font-medium mb-2 md:mb-3 text-sm md:text-base flex items-center">
                        <FontAwesomeIcon icon={faUser} className="w-3 h-3 md:w-4 md:h-4 mr-2 text-gray-600" />
                        Nombre del Comprador *
                      </label>
                      <input
                        type="text"
                        value={giftCardData.comprador_nombre}
                        onChange={(e) => setGiftCardData({...giftCardData, comprador_nombre: e.target.value})}
                        className="w-full px-3 py-2 md:px-4 md:py-3 text-sm md:text-base bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                        placeholder="Juan Pérez"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-black font-medium mb-2 md:mb-3 text-sm md:text-base flex items-center">
                        <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3 md:w-4 md:h-4 mr-2 text-gray-600" />
                        Email del Comprador *
                      </label>
                      <input
                        type="email"
                        value={giftCardData.comprador_email}
                        onChange={(e) => setGiftCardData({...giftCardData, comprador_email: e.target.value})}
                        className="w-full px-3 py-2 md:px-4 md:py-3 text-sm md:text-base bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                        placeholder="juan@email.com"
                        required
                      />
                    </div>
                    
                    {/* Teléfono del Comprador */}
                    <div>
                      <label className="block text-black font-medium mb-2 md:mb-3 text-sm md:text-base flex items-center">
                        <FontAwesomeIcon icon={faPhone} className="w-3 h-3 md:w-4 md:h-4 mr-2 text-gray-600" />
                        Teléfono del Comprador *
                      </label>
                      <input
                        type="tel"
                        value={giftCardData.comprador_telefono}
                        onChange={(e) => setGiftCardData({...giftCardData, comprador_telefono: e.target.value})}
                        className="w-full px-3 py-2 md:px-4 md:py-3 text-sm md:text-base bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                        placeholder="+53 1234 5678"
                        required
                      />
                    </div>
                  </div>

                  {/* Destinatario */}
                  <div>
                    <label className="block text-black font-medium mb-2 md:mb-3 text-sm md:text-base flex items-center">
                      <FontAwesomeIcon icon={faGift} className="w-3 h-3 md:w-4 md:h-4 mr-2 text-gray-600" />
                      Nombre del Destinatario *
                    </label>
                    <input
                      type="text"
                      value={giftCardData.destinatario_nombre}
                      onChange={(e) => setGiftCardData({...giftCardData, destinatario_nombre: e.target.value})}
                      className="w-full px-3 py-2 md:px-4 md:py-3 text-sm md:text-base bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                      placeholder="María González"
                      required
                    />
                  </div>

                  {/* Mensaje */}
                  <div>
                    <label className="block text-black font-medium mb-2 md:mb-3 text-sm md:text-base">
                      Mensaje Personalizado *
                    </label>
                    <textarea
                      value={giftCardData.mensaje_personalizado}
                      onChange={(e) => setGiftCardData({...giftCardData, mensaje_personalizado: e.target.value})}
                      rows="3"
                      maxLength="200"
                      className="w-full px-3 py-2 md:px-4 md:py-3 text-sm md:text-base bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all resize-none shadow-sm"
                      placeholder="¡Feliz cumpleaños! Disfruta de una experiencia gastronómica única en ADASTRA."
                      required
                    />
                    <p className="text-xs md:text-sm text-gray-600 mt-2">
                      {giftCardData.mensaje_personalizado.length}/200 caracteres
                    </p>
                  </div>

                  {/* Validez */}
                  <div>
                    <label className="block text-black font-medium mb-2 md:mb-3 text-sm md:text-base flex items-center">
                      <FontAwesomeIcon icon={faCalendar} className="w-3 h-3 md:w-4 md:h-4 mr-2 text-gray-600" />
                      Validez (meses)
                    </label>
                    <select
                      value={giftCardData.meses_validez}
                      onChange={(e) => setGiftCardData({...giftCardData, meses_validez: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                    >
                      <option value={6} className="bg-white">6 meses</option>
                      <option value={12} className="bg-white">12 meses (recomendado)</option>
                      <option value={18} className="bg-white">18 meses</option>
                      <option value={24} className="bg-white">24 meses</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-black text-white py-3 md:py-4 px-6 md:px-8 rounded-xl text-sm md:text-base font-medium hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transform hover:scale-105 shadow-lg"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-b-2 border-white"></div>
                        <span>Creando...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faStar} className="w-4 h-4" />
                        <span>Crear Gift Card</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Validar/Usar Gift Card Tab - Más visible */}
            {activeTab === 'usar' && (
              <div className="bg-gray-50 backdrop-blur-sm rounded-2xl border-2 border-gray-200 p-8 lg:p-12 shadow-xl">
                <div className="flex items-center mb-8">
                  <div className="bg-gray-200 backdrop-blur-sm rounded-full p-3 mr-4 border border-gray-300">
                    <FontAwesomeIcon icon={faCreditCard} className="h-6 w-6 text-gray-700" />
                  </div>
                  <h2 className="text-3xl font-light text-black">Validar / Usar Gift Card</h2>
                </div>
                
                <div className="space-y-8">
                  {/* Código */}
                  <div>
                    <label className="block text-black font-medium mb-4 flex items-center">
                      <FontAwesomeIcon icon={faSearch} className="w-4 h-4 mr-2 text-gray-600" />
                      Código de Gift Card *
                    </label>
                    <div className="flex gap-4">
                      <input
                        type="text"
                        value={codigo}
                        onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                        className="flex-1 px-4 py-3 bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                        placeholder="GCABCD1234"
                        maxLength="12"
                      />
                      <button
                        type="button"
                        onClick={handleValidarGiftCard}
                        disabled={loading}
                        className="bg-gray-200 hover:bg-gray-300 text-black px-8 py-3 rounded-xl font-medium transition-all duration-300 disabled:opacity-50 flex items-center space-x-2 border-2 border-gray-300 shadow-sm"
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                        ) : (
                          <FontAwesomeIcon icon={faSearch} className="w-4 h-4" />
                        )}
                        <span>{loading ? 'Validando...' : 'Validar'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Información de la Gift Card */}
                  {giftCardInfo && (
                    <div className={`p-6 rounded-xl border-2 shadow-lg ${
                      giftCardInfo.valida 
                        ? 'bg-green-50 border-green-400' 
                        : 'bg-red-50 border-red-400'
                    }`}>
                      <h3 className="font-bold text-xl mb-4 text-black flex items-center">
                        <FontAwesomeIcon 
                          icon={giftCardInfo.valida ? faCheck : faTimes} 
                          className={`w-5 h-5 mr-2 ${giftCardInfo.valida ? 'text-green-600' : 'text-red-600'}`} 
                        />
                        {giftCardInfo.valida ? 'Gift Card Válida' : 'Gift Card No Válida'}
                      </h3>
                      <div className="grid grid-cols-2 gap-6 text-sm">
                        <div className="space-y-3">
                          <div className="flex justify-between border-b border-gray-200 pb-2">
                            <span className="text-gray-600 font-medium">Código:</span> 
                            <span className="text-black font-bold">{giftCardInfo.codigo}</span>
                          </div>
                          <div className="flex justify-between border-b border-gray-200 pb-2">
                            <span className="text-gray-600 font-medium">Estado:</span> 
                            <span className="text-black font-bold">{giftCardInfo.estado}</span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between border-b border-gray-200 pb-2">
                            <span className="text-gray-600 font-medium">Saldo:</span> 
                            <span className="text-black font-bold text-xl">${giftCardInfo.saldo_disponible}</span>
                          </div>
                          <div className="flex justify-between border-b border-gray-200 pb-2">
                            <span className="text-gray-600 font-medium">Expira:</span> 
                            <span className="text-black font-bold">
                              {new Date(giftCardInfo.fecha_expiracion).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Usar Gift Card */}
                  {giftCardInfo && giftCardInfo.valida && (
                    <div className="border-t-2 border-gray-200 pt-8">
                      <h3 className="text-xl font-light text-black mb-6 flex items-center">
                        <FontAwesomeIcon icon={faDollarSign} className="w-5 h-5 mr-2 text-gray-600" />
                        Usar Gift Card
                      </h3>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-black font-medium mb-3">
                            Monto a Usar * (Max: ${giftCardInfo.saldo_disponible})
                          </label>
                          <input
                            type="number"
                            value={montoUso}
                            onChange={(e) => setMontoUso(e.target.value)}
                            max={giftCardInfo.saldo_disponible}
                            min="0.01"
                            step="0.01"
                            className="w-full px-4 py-3 bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                            placeholder="25.00"
                          />
                        </div>
                        <div>
                          <label className="block text-black font-medium mb-3">
                            Concepto
                          </label>
                          <input
                            type="text"
                            value={conceptoUso}
                            onChange={(e) => setConceptoUso(e.target.value)}
                            className="w-full px-4 py-3 bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                            placeholder="Comida en restaurante"
                          />
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleUsarGiftCard}
                        disabled={loading || !montoUso}
                        className="w-full bg-red-500 hover:bg-red-600 text-white py-4 px-8 rounded-xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span>Procesando...</span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faCreditCard} />
                            <span>Usar Gift Card</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Información del proceso - Más visible */}
          <div className="max-w-4xl mx-auto mt-16 bg-gray-50 backdrop-blur-sm rounded-2xl p-8 border-2 border-gray-200 shadow-xl">
            <div className="flex items-center mb-6">
              <div className="bg-gray-200 backdrop-blur-sm rounded-full p-2 mr-3 border border-gray-300">
                <FontAwesomeIcon icon={faStar} className="h-4 w-4 text-gray-700" />
              </div>
              <h3 className="text-xl font-light text-black">¿Cómo Funciona?</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-gray-700 text-sm">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold text-white mt-0.5 shadow-lg">1</div>
                  <div>
                    <h4 className="text-black font-semibold mb-1">Crea tu Gift Card</h4>
                    <p>Selecciona el valor, completa los datos del destinatario y personaliza tu mensaje</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold text-white mt-0.5 shadow-lg">2</div>
                  <div>
                    <h4 className="text-black font-semibold mb-1">Entrega al Destinatario</h4>
                    <p>Comparte la gift card con la persona especial de la manera que prefieras</p>
                  </div>
                </div>
                <div className="bg-gray-100 rounded-lg p-4 border-2 border-gray-300 shadow-sm">
                  <h4 className="text-black font-semibold mb-2 flex items-center">
                    <FontAwesomeIcon icon={faGift} className="w-4 h-4 mr-2 text-gray-600" />
                    Válida por 12 meses
                  </h4>
                  <p className="text-xs text-gray-700">Cada gift card tiene un código único y es válida en ADASTRA Restaurant, Obispo 255, La Habana</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold text-white mt-0.5 shadow-lg">3</div>
                  <div>
                    <h4 className="text-black font-semibold mb-1">Uso en el Restaurante</h4>
                    <p>El destinatario presenta el código en ADASTRA para disfrutar su experiencia gastronómica</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold text-white mt-0.5 shadow-lg">4</div>
                  <div>
                    <h4 className="text-black font-semibold mb-1">Uso Flexible</h4>
                    <p>Se puede usar total o parcialmente, con saldo restante disponible para futuras visitas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action - Reserva tu Experiencia */}
      <section className="relative py-20 overflow-hidden bg-white">
        <div className="container-custom max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Side - Content */}
            <div className="text-center lg:text-left">
              <h2 className="font-sans text-4xl md:text-5xl font-light text-gray-900 mb-6">
                Reserva tu <span className="font-normal italic text-green-600">Experiencia</span>
              </h2>

              <p className="font-sans text-lg text-gray-600 mb-8 max-w-2xl">
                Cada momento en ADASTRA es único. No dejes que se escape tu próxima experiencia inolvidable.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link 
                  to="/reservas"
                  className="bg-white text-black border border-gray-300 py-2 md:py-3 px-6 md:px-8 rounded-full text-sm md:text-base font-medium hover:bg-gray-100 transition-all duration-300 flex items-center justify-center space-x-2 shadow-md"
                >
                  <FontAwesomeIcon icon={faCalendar} className="w-4 h-4" />
                  <span>Reservar Mesa</span>
                </Link>
                
                <a 
                  href="https://wa.me/5363518498?text=Hola%20ADASTRA,%20me%20interesa%20hacer%20una%20reserva%20o%20consulta."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-black border border-gray-300 py-2 md:py-3 px-6 md:px-8 rounded-full text-sm md:text-base font-medium hover:bg-gray-100 transition-all duration-300 flex items-center justify-center space-x-2 shadow-md"
                >
                  <FontAwesomeIcon icon={faPhone} className="w-4 h-4" />
                  <span>Contactar Ahora</span>
                </a>
              </div>
            </div>

            {/* Right Side - Image Carousel */}
            <div className="relative">
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                {/* Carousel Images */}
                <div className="relative w-full h-full">
                  <img
                    src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Entrada%20con%20jarron%20y%20colecion.JPG"
                    alt="Interior ADASTRA Restaurant"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Modal de Descarga de Gift Card - Mantenemos el fondo negro para el modal */}
      {showDownloadModal && createdGiftCard && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-gray-200 p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            
            {/* Header del Modal */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center">
                <div className="bg-black/10 backdrop-blur-sm rounded-full p-3 mr-4">
                  <FontAwesomeIcon icon={faGift} className="h-6 w-6 text-black" />
                </div>
                <h2 className="text-3xl font-light text-black">Gift Card Creada</h2>
              </div>
              <button
                onClick={() => setShowDownloadModal(false)}
                className="text-gray-600 hover:text-black transition-colors p-2"
              >
                <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
              </button>
            </div>

            {/* Instrucciones de Uso - Movidas arriba */}
            <div className="mb-8 bg-black/5 backdrop-blur-sm rounded-xl p-6 border border-black/10">
              <h3 className="text-black font-medium mb-3 flex items-center">
                <FontAwesomeIcon icon={faStar} className="w-4 h-4 mr-2 text-gray-600" />
                Instrucciones de Uso
              </h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• <strong>Siguiente:</strong> Tras realizar la orden de compra, nuestro equipo le contactará por WhatsApp para coordinar el pago y enviarle la Giftcard digital.</li>
                <li>• <strong>Código:</strong> El destinatario debe presentar el código en el restaurante</li>
                <li>• <strong>Uso:</strong> Válida para consumo en ADASTRA Restaurant, La Habana</li>
              </ul>
            </div>

            {/* Gift Card Visual Design - Background negro */}
            <div className="mb-8">
              <div 
                ref={giftCardRef}
                className="gift-card-print bg-black rounded-2xl p-8 border-2 border-white/20 mx-auto max-w-2xl relative overflow-hidden"
              >
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div 
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `
                        linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
                      `,
                      backgroundSize: '30px 30px'
                    }}
                  />
                </div>

                {/* Header */}
                <div className="relative z-10 text-center mb-8">
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                      <FontAwesomeIcon icon={faGift} className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <h1 className="text-4xl font-light text-white mb-2">ADASTRA RESTAURANT</h1>
                  <p className="text-gray-300 text-lg">Gift Card</p>
                </div>

                {/* Código y Valor */}
                <div className="relative z-10 text-center mb-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 mb-6">
                    <p className="text-gray-300 text-sm mb-2">CÓDIGO</p>
                    <p className="text-white text-3xl font-mono font-bold tracking-widest">
                      XXXXXXXXXX
                    </p>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <p className="text-gray-300 text-sm mb-2">VALOR</p>
                    <p className="text-white text-5xl font-light">
                      ${createdGiftCard.valor}
                    </p>
                  </div>
                </div>

                {/* Información Personal */}
                <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {createdGiftCard.comprador_nombre && (
                    <div className="text-center">
                      <p className="text-gray-400 text-sm mb-1">DE</p>
                      <p className="text-white font-medium">{createdGiftCard.comprador_nombre}</p>
                    </div>
                  )}
                  {createdGiftCard.destinatario_nombre && (
                    <div className="text-center">
                      <p className="text-gray-400 text-sm mb-1">PARA</p>
                      <p className="text-white font-medium">{createdGiftCard.destinatario_nombre}</p>
                    </div>
                  )}
                </div>

                {/* Mensaje Personalizado */}
                {createdGiftCard.mensaje_personalizado && (
                  <div className="relative z-10 text-center mb-8">
                    <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                      <p className="text-gray-400 text-sm mb-2">MENSAJE</p>
                      <p className="text-white italic text-lg leading-relaxed">
                        "{createdGiftCard.mensaje_personalizado}"
                      </p>
                    </div>
                  </div>
                )}

                {/* Footer Info */}
                <div className="relative z-10 text-center pt-6 border-t border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-300">
                    <div>
                      <p className="font-medium">Válida hasta:</p>
                      <p>{new Date(createdGiftCard.fecha_expiracion).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</p>
                    </div>
                    <div>
                      <p className="font-medium">Ubicación:</p>
                      <p>Obispo 255, La Habana Cuba</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <p className="text-gray-400 text-xs">
                      Presentar código en el restaurante • No reembolsable • Transferible
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Botones de Acción - Adaptados para fondo blanco */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="flex items-center justify-center space-x-2 bg-black text-white px-8 py-3 rounded-xl font-medium hover:bg-gray-800 transition-all duration-300 transform hover:scale-105"
              >
                <FontAwesomeIcon icon={faHome} />
                <span>HOME</span>
              </Link>
              
              <button
                onClick={() => setShowDownloadModal(false)}
                className="flex items-center justify-center space-x-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-xl font-medium transition-all duration-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>CERRAR</span>
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default GiftCards;